import { useEffect, useMemo, useRef, useState } from "react";
import { previewFxRequirements } from "../../lib/api";
import { buildCalcPayload } from "../../lib/calcPayload";
import { derivePreviewFxPairs, derivePreviewMonthlyFxPairs } from "./flaggedFx";

const mergePairs = (a, b) => {
  const seen = new Set();
  const out = [];
  for (const [src, tgt] of [...a, ...b]) {
    const key = `${src}|${tgt}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push([src, tgt]);
  }
  return out;
};

// v0.6 Stage 2 (extracted from CalculatePanel.jsx to stay under its LOC
// cap) — pre-calc FX-requirements preview (POST /api/calculate/fx-preview).
// Debounced: fires ~500ms after run/period/registration selection settles,
// not on every keystroke, and never on an FX-rate edit itself (rates
// aren't in the trigger key below — the preview ignores them server-side
// anyway, so re-firing on every digit typed would just be wasted
// classification work for no new answer). Read-only and best-effort: a
// failed/slow preview never blocks the Run button — the post-calc
// missing-rate flow (unchanged) is still the real safety net. Merges its
// findings with the review-queue-derived flags rather than replacing
// them, so a recalc triggered from the review queue still works exactly
// as before.
export function useFxPreview({
  registrations, selected, periodStart, periodEnd, countryFilter,
  regime, clientId, noRegistrations,
  flaggedFxPairs, flaggedMonthlyFxPairs,
}) {
  const [fxPreview, setFxPreview] = useState(null);
  const [fxPreviewLoading, setFxPreviewLoading] = useState(false);
  const fxPreviewSeq = useRef(0);
  const periodValidForPreview = !!periodStart && !!periodEnd && periodStart <= periodEnd;
  const fxPreviewTriggerKey = (periodValidForPreview && selected.length > 0 && !noRegistrations)
    ? [...selected].sort().join(",") + "|" + periodStart + "|" + periodEnd
      + "|" + regime + "|" + (clientId || "")
    : null;

  useEffect(() => {
    if (!fxPreviewTriggerKey) {
      setFxPreview(null);
      setFxPreviewLoading(false);
      return undefined;
    }
    const seq = ++fxPreviewSeq.current;
    setFxPreviewLoading(true);
    const timer = setTimeout(() => {
      previewFxRequirements(buildCalcPayload({
        registrations, runIds: selected,
        periodStart, periodEnd, countryFilter,
        periodFxRates: {}, monthlyFxRates: {}, clientId,
      }))
        .then((data) => {
          if (fxPreviewSeq.current !== seq) return; // superseded by a newer trigger
          setFxPreview(data);
        })
        .catch(() => {
          // Fail soft — advisory only, the Run button and post-calc flow
          // still work without a preview answer.
          if (fxPreviewSeq.current === seq) setFxPreview(null);
        })
        .finally(() => {
          if (fxPreviewSeq.current === seq) setFxPreviewLoading(false);
        });
    }, 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fxPreviewTriggerKey]);

  const previewFxPairs = useMemo(
    () => derivePreviewFxPairs(fxPreview?.fx_requirements), [fxPreview],
  );
  const previewMonthlyFxPairs = useMemo(
    () => derivePreviewMonthlyFxPairs(fxPreview?.fx_requirements), [fxPreview],
  );
  const mergedFlaggedFxPairs = useMemo(
    () => mergePairs(flaggedFxPairs, previewFxPairs),
    [flaggedFxPairs, previewFxPairs],
  );
  const mergedFlaggedMonthlyFxPairs = useMemo(
    () => mergePairs(flaggedMonthlyFxPairs, previewMonthlyFxPairs),
    [flaggedMonthlyFxPairs, previewMonthlyFxPairs],
  );

  return {
    fxPreviewLoading,
    mayBeIncomplete: !!fxPreview?.may_be_incomplete,
    // A2-467 review — "no exchange rates needed" may only be said once the
    // preview has ANSWERED: it is null while loading, on failure, and with
    // nothing selected, and the declared-currency rule alone finds nothing
    // for a client_template upload (no declared_currency).
    previewAnswered: fxPreview != null,
    mergedFlaggedFxPairs,
    mergedFlaggedMonthlyFxPairs,
  };
}
